"use client";

import React, { useEffect, useState } from "react";
import {
  bookingSectionTitleStyle,
  TEMPLATE_SPACING,
} from "@openings/template-components";
import { formatDuration } from "@openings/booking-core";

import { ServiceItemRow } from "./ServiceItemRow";
import { serviceItemRowStyles } from "./bookingTextStyles";
import { standaloneCardTitleStyle } from "./editorialBookingStyles";

export type EditorialLedgerService = {
  id: string;
  title: string;
  description?: string;
  price?: number;
  up?: boolean;
  duration?: number;
};

export type EditorialLedgerGroup = {
  key: string;
  name: string | null;
  services: EditorialLedgerService[];
};

type EditorialServiceLedgerProps = {
  groups: EditorialLedgerGroup[];
  className?: string;
  contentId?: string;
};

/**
 * Shared services-and-pricing surface for standalone booking pages.
 *
 * Desktop renders the full editorial ledger. Mobile keeps the same content
 * available behind a compact, accessible disclosure.
 */
export function EditorialServiceLedger({
  groups,
  className,
  contentId = "openings-booking-services-ledger-content",
}: EditorialServiceLedgerProps) {
  const [isOpen, setIsOpen] = useState(false);
  // The toggle ships disabled in the server markup and enables once mounted.
  // A tap before hydration otherwise hit a button with no handler yet and was
  // silently dropped, leaving the ledger closed (Oct 2026: the nightly web
  // responsive.spec.ts flake). Same rule as the dashboard scheduler slots.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  return (
    <section
      className={["openings-booking-ledger", className]
        .filter(Boolean)
        .join(" ")}
      data-service-ledger
      aria-label="Services and pricing"
    >
      <style>{`
        .openings-booking-ledger {
          box-sizing: border-box;
          grid-area: ledger;
          min-width: 0;
          width: 100%;
          padding: var(--openings-booking-editorial-card-padding, 32px);
          border: 1px solid var(--openings-color-border, #e5e5e5);
          box-shadow: var(--openings-booking-surface-shadow, none);
          border-radius: var(--openings-booking-radius, 10px);
          background: var(--openings-color-surface, #fff);
        }
        .openings-booking-ledger h2,
        .openings-booking-ledger h3 {
          color: var(--openings-color-text, #111);
        }
        .openings-booking-ledger-mobile-heading {
          display: none;
          margin: 0;
          border-radius: inherit;
        }
        .openings-booking-ledger-mobile-toggle {
          display: none;
        }
        .openings-booking-service-groups {
          display: flex;
          flex-direction: column;
          gap: var(--openings-booking-spacing-xl, 24px);
        }
        .openings-booking-service-list {
          display: flex;
          flex-direction: column;
        }
        .openings-booking-service-row {
          padding: var(--openings-booking-spacing-lg, 24px) 0;
          border-bottom: 1px solid
            var(--openings-booking-divider-color, #f0f0f0);
        }
        .openings-booking-service-row:first-child {
          padding-top: 0;
        }
        .openings-booking-service-row:last-child {
          padding-bottom: 0;
          border-bottom: 0;
        }

        @media (max-width: 991px) {
          .openings-booking-ledger {
            padding: 0;
          }
          .openings-booking-ledger-desktop-heading {
            display: none;
          }
          .openings-booking-ledger-mobile-heading {
            display: block;
          }
          .openings-booking-ledger-mobile-toggle {
            display: flex;
            width: 100%;
            min-height: 64px;
            padding: var(--openings-booking-spacing-lg, 20px)
              var(--openings-booking-editorial-card-padding, 16px);
            align-items: center;
            justify-content: space-between;
            gap: var(--openings-booking-spacing-lg, 16px);
            border: 0;
            border-radius: inherit;
            background: transparent;
            color: var(--openings-color-text, #111);
            font: inherit;
            font-size: var(--openings-booking-card-title-font-size, 22px);
            font-weight:
              var(--openings-booking-card-title-font-weight, 700);
            line-height:
              var(--openings-booking-card-title-line-height, 1.25);
            text-align: left;
            cursor: pointer;
          }
          .openings-booking-ledger-mobile-toggle svg {
            flex: 0 0 auto;
            transition: transform 160ms ease;
          }
          .openings-booking-ledger-mobile-toggle[aria-expanded="true"] svg {
            transform: rotate(180deg);
          }
          .openings-booking-ledger-mobile-toggle:focus-visible {
            outline: 2px solid var(--openings-color-text, #111);
            outline-offset: -2px;
          }
          .openings-booking-ledger-content {
            display: none;
            padding: 0
              var(--openings-booking-editorial-card-padding, 16px)
              var(--openings-booking-editorial-card-padding, 16px);
          }
          .openings-booking-ledger-content--open {
            display: block;
          }
          .openings-booking-service-groups {
            gap: var(--openings-booking-spacing-xl, 24px);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .openings-booking-ledger-mobile-toggle svg {
            transition: none;
          }
        }
      `}</style>

      <h2
        className="openings-booking-ledger-desktop-heading"
        style={{
          ...standaloneCardTitleStyle,
          margin: "0 0 var(--openings-booking-spacing-lg, 16px) 0",
        }}
      >
        Services &amp; pricing
      </h2>
      <h2
        className="openings-booking-ledger-mobile-heading"
        style={standaloneCardTitleStyle}
      >
        <button
          type="button"
          className="openings-booking-ledger-mobile-toggle"
          data-service-ledger-toggle
          aria-expanded={isOpen}
          aria-controls={contentId}
          disabled={!hydrated}
          onClick={() => setIsOpen((open) => !open)}
        >
          <span>Services &amp; pricing</span>
          <svg
            aria-hidden="true"
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="m3.5 6 4.5 4 4.5-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </h2>
      <div
        id={contentId}
        className={`openings-booking-ledger-content${
          isOpen ? " openings-booking-ledger-content--open" : ""
        }`}
        data-service-ledger-content
      >
        <div className="openings-booking-service-groups">
          {groups.map((group) => (
            <div key={group.key}>
              {group.name ? (
                <h3
                  style={{
                    ...bookingSectionTitleStyle,
                    margin: `0 0 ${TEMPLATE_SPACING.sm} 0`,
                  }}
                >
                  {group.name}
                </h3>
              ) : null}
              <div className="openings-booking-service-list">
                {group.services.map((service) => (
                  <ServiceItemRow
                    key={service.id}
                    title={service.title}
                    price={service.price}
                    up={service.up}
                    durationLabel={
                      service.duration != null
                        ? formatDuration(service.duration)
                        : undefined
                    }
                    description={service.description}
                    {...serviceItemRowStyles}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
